import { useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { promotionsData, Promotion } from "../../../data/menuOversightData";
import Pagination from "../../../components/Pagination";

const PromotionsTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter promotions based on search and status
  const filteredPromotions = promotionsData.filter((promotion) => {
    const matchesSearch = promotion.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promotion.restaurant.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || promotion.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalItems = filteredPromotions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPromotions = filteredPromotions.slice(startIndex, endIndex);

  const handlePagination = (page: number) => {
    setCurrentPage(page);
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending_approval":
        return "text-orange-600 bg-orange-100";
      case "active":
        return "bg-[#008B501F] text-secondary";
      case "rejected":
        return "text-red-500 bg-[#EA19191F]";
      case "expired":
        return "text-gray-500 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending_approval":
        return "Pending Approval";
      case "active":
        return "Active";
      case "rejected":
        return "Rejected";
      case "expired":
        return "Expired";
      default:
        return status;
    }
  };

  const handleAction = (promotionId: string, action: "approve" | "reject" | "deactivate") => {
    console.log(`${action} promotion:`, promotionId);
    // This would normally make an API call
  };

  return (
    <div className="w-full">
      {/* Search and Filter Section */}
      <div className="my-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 pl-10 text-sm w-full focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
            placeholder="Search promotions..."
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          >
            <option value="all">All Status</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="active">Active</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm || statusFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            className="text-sm text-secondary hover:text-secondary-dark underline"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Promotions Content */}
      <div className="space-y-4">
        {paginatedPromotions.length > 0 ? (
          paginatedPromotions.map((promotion) => (
            <div
              key={promotion.id}
              className="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg text-secondary">
                      {promotion.title}
                    </h3>
                    <span
                      className={`${getStatusColor(
                        promotion.status
                      )} text-xs font-medium px-2 py-1 rounded`}
                    >
                      {getStatusText(promotion.status)}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-3">{promotion.description}</p>
                  <div className="flex gap-6 text-sm">
                    <span className="text-orange-500">
                      <strong>Valid until:</strong> {promotion.validUntil}
                    </span>
                    <span className="text-gray-500">
                      <strong>Submitted:</strong> {promotion.submitted}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">{promotion.restaurant}</p>
                </div>
                <div className="flex gap-2">
                  {promotion.status === "pending_approval" && (
                    <>
                      <button
                        onClick={() => handleAction(promotion.id, "approve")}
                        className="bg-secondary text-white px-4 py-2 rounded-md text-sm hover:bg-secondary-dark transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleAction(promotion.id, "reject")}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm hover:bg-red-600 transition-colors"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {promotion.status === "active" && (
                    <button
                      onClick={() => handleAction(promotion.id, "deactivate")}
                      className="bg-red-500 text-white px-4 py-2 rounded-md text-sm hover:bg-red-600 transition-colors"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-500">
              {searchTerm || statusFilter !== "all"
                ? "No promotions match your filters."
                : "No promotions found."}
            </p>
            {(searchTerm || statusFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
                className="text-secondary hover:underline text-sm mt-2"
              >
                Clear filters to see all promotions
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredPromotions.length > 0 && (
        <div className="mt-6">
          <Pagination
            itemsPerPage={itemsPerPage}
            length={totalItems}
            currentPage={currentPage}
            handlePagination={handlePagination}
            prevPage={prevPage}
            nextPage={nextPage}
          />
        </div>
      )}
    </div>
  );
};

export default PromotionsTable;